import { BetaTag } from '@client/components/_ui/display/BetaTag';
import { cn } from '@client/utils/styling/cn';
import { createLink, type LinkComponent } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { cloneElement, forwardRef, isValidElement, useMemo } from 'react';
import { LinkTooltip } from './LinkTooltip';
import type { ButtonLinkProps } from './types';

const sizeClasses = {
    micro: 'btn-micro',
    small: 'btn-sm',
    middle: 'btn-md',
    large: 'btn-lg',
} as const;

const typeClasses = {
    primary: 'btn-modern-primary',
    secondary: 'btn-modern-secondary',
    tertiary: 'btn-modern-tertiary',
    dashed: 'btn-modern-dashed',
    danger: 'btn-modern-danger',
    'danger-filled': 'btn-modern-danger-filled',
    'danger-outline': 'btn-modern-danger-outline',
    link: 'btn-modern-link',
} as const;

const iconSizes = {
    micro: 12,
    small: 14,
    middle: 16,
    large: 20,
} as const;

/**
 * Base anchor component styled as a modern button
 * This is wrapped by createLink for type-safe routing
 */
const ButtonLinkBase = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
    (
        {
            variant = 'link',
            size = 'middle',
            icon,
            iconPosition = 'start',
            loading = false,
            iconOnly,
            block = false,
            disabled,
            disabledReason,
            className,
            children,
            badge,
            beta,
            danger,
            color,
            ...props
        },
        ref,
    ) => {
        const isIconOnly = iconOnly ?? (!children && !!icon);

        const linkClasses = useMemo(() => {
            const baseClass = isIconOnly
                ? variant === 'tertiary'
                    ? 'btn-modern-icon-tertiary'
                    : variant === 'secondary' || variant === 'danger-outline'
                      ? typeClasses[variant]
                      : 'btn-modern-icon'
                : typeClasses[variant];

            // Default to 'accent' for primary variant when no color specified
            const effectiveColor = color ?? (variant === 'primary' ? 'accent' : undefined);

            return cn(
                'btn-variant',
                'whitespace-nowrap',
                baseClass,
                sizeClasses[size],
                // Only text-bearing variants need the square treatment — the
                // dedicated icon classes are already square via symmetric
                // padding, and a block button must keep its full width.
                isIconOnly &&
                    !block &&
                    (baseClass === 'btn-modern-secondary' || baseClass === 'btn-modern-danger-outline') &&
                    'btn-icon-only',
                effectiveColor && `btn-color-${effectiveColor}`,
                danger && 'btn-danger',
                loading && 'loading',
                block && 'w-full',
                disabled && 'pointer-events-none opacity-50',
                className,
            );
        }, [variant, size, isIconOnly, color, danger, loading, block, disabled, className]);

        const renderIcon = () => {
            if (loading) {
                return <Loader2 className="btn-icon animate-spin" size={iconSizes[size]} />;
            }
            if (icon) {
                const sizedIcon = isValidElement(icon)
                    ? cloneElement(icon as React.ReactElement<{ size?: number }>, { size: iconSizes[size] })
                    : icon;
                return <span className="btn-icon">{sizedIcon}</span>;
            }
            return null;
        };

        return (
            <LinkTooltip disabled={disabled} disabledReason={disabledReason} block={block}>
                <a
                    ref={ref}
                    aria-disabled={disabled || loading}
                    aria-busy={loading}
                    tabIndex={disabled ? -1 : undefined}
                    className={linkClasses}
                    {...props}
                >
                    {iconPosition === 'start' && renderIcon()}
                    {children}
                    {beta && <BetaTag />}
                    {badge}
                    {iconPosition === 'end' && renderIcon()}
                </a>
            </LinkTooltip>
        );
    },
);

ButtonLinkBase.displayName = 'ButtonLinkBase';

const CreatedButtonLink = createLink(ButtonLinkBase);

/**
 * Button.Link - A button-styled link with type-safe routing
 *
 * Renders as an anchor element for proper navigation semantics,
 * but styled as a modern button. Uses TanStack Router for type-safe
 * `to`, `params`, and `search` props.
 * Icons are automatically sized based on the button size (micro: 12px, small: 14px, middle: 16px, large: 20px).
 *
 * Note: Uses `variant` prop instead of `type` to avoid conflict with
 * HTML anchor's native `type` attribute when used with TanStack Router's createLink.
 *
 * @example
 * // Primary navigation button
 * <Button.Link to="/claims/$claimId" params={{ claimId: '123' }} variant="primary">
 *   View Claim
 * </Button.Link>
 *
 * @example
 * // Secondary link with icon (size auto-applied)
 * <Button.Link to="/settings" variant="secondary" icon={<Settings />}>
 *   Settings
 * </Button.Link>
 *
 * @example
 * // Tertiary link (no border, text-style)
 * <Button.Link to="/details" variant="tertiary" icon={<Plus />}>
 *   Add Item
 * </Button.Link>
 *
 * @example
 * // Icon-only link button (size auto-applied)
 * <Button.Link to="/notifications" icon={<Bell />} />
 *
 * @example
 * // Disable preloading for heavy pages
 * <Button.Link to="/heavy-page" preload={false}>Heavy Page</Button.Link>
 */
export const ButtonLink: LinkComponent<typeof ButtonLinkBase> = (({ preload, ...props }) => {
    return <CreatedButtonLink preload={preload ?? 'intent'} {...(props as any)} />;
}) as LinkComponent<typeof ButtonLinkBase>;
